import type { AppData } from '@/types/app';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';

/**
 * Computes the <title> from page data.
 *
 * Single source of truth for SSR (render.tsx) and the client
 * (App.tsx useEffect). Without this, the hydration useEffect overwrites
 * the SSR title with `data.site.name` on archives, because it doesn't
 * have the right fallback (seen on /auteur/X/).
 */
export function buildPageTitle(data: AppData): string {
  const siteName = data.site?.name ?? '';
  const currentPage = data.pagination?.current_page ?? 1;
  const totalPages = data.pagination?.total_pages ?? 1;
  const paginationSuffix = data.context?.is_archive && currentPage > 1
    ? ` - Page ${currentPage} sur ${totalPages}`
    : '';

  if (data.context?.is_404) {
    return `404 - ${siteName}`;
  }

  if (data.context?.is_archive) {
    if (data.yoast?.title) {
      return `${decodeHtmlEntities(data.yoast.title)}${paginationSuffix}`;
    }
    if (data.archive?.title) {
      return `${decodeHtmlEntities(data.archive.title)} - ${siteName}${paginationSuffix}`;
    }
    return `${siteName}${paginationSuffix}`;
  }

  if (data.yoast?.title) {
    return decodeHtmlEntities(data.yoast.title);
  }

  if (data.post?.title) {
    return `${decodeHtmlEntities(data.post.title)} - ${siteName}`;
  }

  return siteName;
}
